import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "consts";
import { AdminResourceDeleteModal, PageMain, TanTable, TanTableColumn, useFilters, usePagination, useSorting } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { useKsefIntegrations, useKsefDashboard, ksefApi, KSeFIntegrationApi } from "api/ksef";
import { useDeleteResourceActions } from "utils/store";

export const KSeFPage = (): JSX.Element => {
  const { setDeleteResource } = useDeleteResourceActions();
  const navigate = useNavigate();

  const { isLoading, isFetching, integrations, invalidate } = useKsefIntegrations();
  const { dashboard } = useKsefDashboard();

  const columns = React.useMemo<TanTableColumn<KSeFIntegrationApi>[]>(() => cols, []);

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <PageMain>
      <PageRichHeader header="KSeF">
        <PaziaButton type="primary" title="Dodaj +" onClick={() => navigate(routes.KSEF_ADD)} />
      </PageRichHeader>

      {dashboard && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <DashboardCard label="Oczekuje" value={dashboard.pending} color="yellow" />
          <DashboardCard label="Wysłano" value={dashboard.submitted} color="blue" />
          <DashboardCard label="Zaakceptowano" value={dashboard.accepted} color="green" />
          <DashboardCard label="Odrzucono" value={dashboard.rejected} color="red" />
          <DashboardCard label="Błędy" value={dashboard.errors} color="red" />
          <DashboardCard label="Ponowna próba" value={dashboard.retryScheduled} color="orange" />
        </div>
      )}

      <TanTable
        data={integrations}
        columns={columns}
        total={integrations.length}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={() => {}}
        onSortingChange={() => {}}
        onDelete={(x) => setDeleteResource(x.id)}
        pagination={{ pageIndex: 0, pageSize: 20 }}
        onEdit={(x) => navigate(routes.KSEF_EDIT(x.id.toString()))}
        sorting={[]}
        onRowClick={(x) => navigate(routes.KSEF_VIEW(x.id.toString()))}
      />
      <AdminResourceDeleteModal
        title="Usuń integrację KSeF"
        description="Jesteś pewien że chcesz usunąć tę integrację KSeF?"
        onSuccessDelete={() => invalidate()}
        deleteAction={ksefApi.delete}
      />
    </PageMain>
  );
};

const colorClasses: Record<string, string> = {
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-green-50 text-green-700 border-green-200",
  red: "bg-red-50 text-red-700 border-red-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
};

const DashboardCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className={`rounded-lg border p-3 text-center ${colorClasses[color] ?? colorClasses.blue}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="mt-0.5 text-xs">{label}</div>
  </div>
);

const cols: TanTableColumn<KSeFIntegrationApi>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    size: 60,
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Nazwa",
    size: 250,
    enableColumnFilter: false,
  },
  {
    id: "environment",
    accessorKey: "environment",
    header: "Środowisko",
    size: 120,
    enableColumnFilter: false,
    cell: (info) => (info.getValue() === 1 ? "Test" : "Produkcja"),
  },
  {
    id: "nip",
    accessorKey: "nip",
    header: "NIP",
    size: 120,
    enableColumnFilter: false,
  },
  {
    id: "isEnabled",
    accessorKey: "isEnabled",
    header: "Status",
    size: 100,
    enableColumnFilter: false,
    cell: (info) => {
      const enabled = info.getValue<boolean>();
      return (
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            enabled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
          }`}
        >
          {enabled ? "Aktywna" : "Nieaktywna"}
        </span>
      );
    },
  },
  {
    id: "createdDate",
    accessorKey: "createdDate",
    header: "Utworzono",
    size: 160,
    enableColumnFilter: false,
    cell: (info) => info.getValue<Date>().toLocaleString(),
  },
];
